import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * IANA timezone the portal renders all time axes / timestamps in. Sourced
 * from the drive row (set in the Drive setup tool). Falls back to
 * Europe/London while the drive query is loading so the very first paint
 * is in a sensible zone.
 */
export function useTimezone(): string {
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  return drive.data?.shiftTimezone ?? "Europe/London";
}

/**
 * Cached `Intl.DateTimeFormat` per timezone.
 *
 * `utcToLocalNaive` is called once per DATA POINT — inside
 * `points.map(...)` on every trace of every chart — and used to build a
 * fresh DateTimeFormat on each call. Constructing one is among the most
 * expensive operations in the JS runtime, so a 24 h trace at the 250 ms
 * sensor scan rate meant hundreds of thousands of constructions per
 * render, blocking the main thread for seconds. Formatters are
 * immutable and safe to share, so one per zone is all we ever need.
 */
const NAIVE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function naiveFormatter(tz: string): Intl.DateTimeFormat {
  const cached = NAIVE_FORMATTERS.get(tz);
  if (cached) return cached;
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };
  let dtf: Intl.DateTimeFormat;
  try {
    dtf = new Intl.DateTimeFormat("en-CA", opts);
  } catch {
    // Bad TZ name — fall back to UTC so we don't crash the chart.
    dtf = new Intl.DateTimeFormat("en-CA", { ...opts, timeZone: "UTC" });
  }
  NAIVE_FORMATTERS.set(tz, dtf);
  return dtf;
}

/**
 * Convert a UTC timestamp to a string Plotly will display at the wall-
 * clock time of `tz`, regardless of how Plotly interprets the input
 * (UTC vs browser-local — its behaviour varies between versions and date
 * formats).
 *
 * The trick: produce the wall-clock numbers in `tz`, then suffix Z. This
 * tells Plotly "treat this as UTC" so it doesn't apply any further
 * timezone shift on display. The user sees the raw digits we built —
 * which, by construction, are the local wall-clock numbers.
 *
 *   "2026-05-06T11:35:00.000Z" + "Europe/London"
 *     -> wall-clock "12:35:00" in BST
 *     -> tagged as UTC: "2026-05-06T12:35:00Z"
 *     -> Plotly displays "12:35" verbatim. Matches user's watch.
 */
export function utcToLocalNaive(
  input: string | Date | null | undefined,
  tz: string,
): string | null {
  if (input == null) return null;
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return typeof input === "string" ? input : null;
  const parts = naiveFormatter(tz).formatToParts(d);
  const m: Record<string, string> = {};
  for (const p of parts) m[p.type] = p.value;
  let hour = m.hour ?? "00";
  if (hour === "24") hour = "00";
  // The Z suffix is intentional. See doc comment above.
  return `${m.year}-${m.month}-${m.day}T${hour}:${m.minute ?? "00"}:${m.second ?? "00"}Z`;
}

/**
 * Inverse of utcToLocalNaive — given a naive datetime-local string typed
 * by the user (e.g. "2026-05-06T10:00") and the IANA zone they meant it
 * in, return the matching UTC ISO string.
 *
 * Uses Intl.DateTimeFormat to determine the zone's offset *at that
 * specific wall-clock time* (so DST boundaries handle correctly), and
 * subtracts it. Avoids the trap where `new Date("...10:00").toISOString()`
 * silently uses the BROWSER's clock zone — which fails if the user's
 * machine TZ isn't BST.
 */
export function localNaiveInputToUtcIso(input: string, tz: string): string {
  const m = input.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return new Date(input).toISOString();
  const y = +m[1]!, mo = +m[2]!, d = +m[3]!, h = +m[4]!, mi = +m[5]!;
  const s = m[6] ? +m[6] : 0;
  // Step 1: Treat the input AS IF it were UTC. Gives a starting ms.
  const asIfUtcMs = Date.UTC(y, mo - 1, d, h, mi, s);
  // Step 2: Ask `tz` what wall-clock it'd show at that ms.
  let dtf: Intl.DateTimeFormat;
  try {
    dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });
  } catch {
    return new Date(asIfUtcMs).toISOString();
  }
  const parts = dtf.formatToParts(new Date(asIfUtcMs));
  const tzMap: Record<string, string> = {};
  for (const p of parts) tzMap[p.type] = p.value;
  let tzHour = +(tzMap.hour ?? "0");
  if (tzHour === 24) tzHour = 0;
  // Step 3: Compute the tz offset at this time.
  const tzWallMs = Date.UTC(
    +(tzMap.year ?? y),
    +(tzMap.month ?? mo) - 1,
    +(tzMap.day ?? d),
    tzHour,
    +(tzMap.minute ?? mi),
    +(tzMap.second ?? s),
  );
  const offsetMs = tzWallMs - asIfUtcMs;
  // Step 4: The user's wall-clock (input) sits offsetMs ahead of UTC,
  // so real UTC ms = asIfUtc − offset.
  return new Date(asIfUtcMs - offsetMs).toISOString();
}


